// Job listing data for all positions

export interface WhatYoullDoSection {
  title: string;
  items: string[];
}

export type FormType = 'engineering' | 'marketing' | 'customer-facing';

export interface JobData {
  slug: string;
  title: string;
  location: string;
  employmentType: string;
  locationType: string;
  department: string;
  compensation: string;
  formType: 'marketing' | 'engineering' | 'customer-facing';
  intro: string[];
  whyHiring: {
    paragraphs: string[];
    emphasis?: string;
  };
  // Support both 30/60/90 format and sections format
  whatYoullDo: {
    day30?: string;
    day60?: string;
    day90?: string;
    sections?: WhatYoullDoSection[];
  };
  // Optional core responsibilities for roles that have both 30/60/90 and bullet points
  coreResponsibilities?: string[];
  whoYouAre: string[];
  whyAyra: { label: string; text: string }[];
  whatWeOffer: string[];
  // Optional custom application form question
  customFormQuestion?: {
    label: string;
    placeholder: string;
  };
}

export const jobListings: Record<string, JobData> = {
  'social-media-brand-manager': {
    slug: 'social-media-brand-manager',
    title: 'Social Media & Brand Manager',
    location: 'Remote (Canada/US)',
    employmentType: 'Full time',
    locationType: 'Remote',
    department: 'Marketing',
    compensation: 'Private',
    formType: 'marketing',
    intro: [
      "We're building AI Employees that handle every call, text, and message — so businesses never miss another customer.",
      "Voice AI that answers, books, and follows up. 24/7. No hold times. No missed opportunities.",
      "We're building AI that gives people their time back."
    ],
    whyHiring: {
      paragraphs: [
        "We've built something real. Now we need the world to know about it.",
        "We're hiring a hungry, creative content machine to turn Ayra into the most talked-about AI company online."
      ],
      emphasis: "Zero fluff. Zero corporate. Just incredible content, every single day."
    },
    whatYoullDo: {
      day30: "Immerse yourself in Ayra — the product, the team, the mission. Start posting daily. Learn what resonates.",
      day60: "Own our TikTok, Instagram, LinkedIn, and YouTube Shorts. Build-in-public content, POV skits, behind-the-scenes moments. Find our viral format.",
      day90: "We're everywhere. You've built a content engine that runs daily, a community that feels like insiders, and a brand people can't stop talking about."
    },
    whoYouAre: [
      "You're chronically online (and proud of it).",
      "You've built a following before — personal brand, client work, side project, whatever.",
      "You can shoot, edit, and post same-day. No hand-holding.",
      "You understand Reels and YouTube Shorts natively — not from a marketing textbook.",
      "You're not a \"social media manager.\" You're a content creator who builds brands.",
      "**Bonus:** You've worked at a startup or built something yourself."
    ],
    whyAyra: [
      { label: "Ground floor:", text: "You're not joining a marketing team. You ARE the marketing team. Build it from scratch." },
      { label: "Equity available:", text: "Opportunity to earn ownership in a company that's actually growing." },
      { label: "Ship daily:", text: "No approval chains. No committees. Make content, post content, learn, repeat." },
      { label: "Remote freedom:", text: "Work from anywhere. We care about output, not hours." },
      { label: "Founder access:", text: "Work directly with the CEO. Your ideas get heard immediately." }
    ],
    whatWeOffer: [
      "**Real stake:** Competitive salary + meaningful equity in a growing company.",
      "**Learning budget:** $10,000/year for courses, tools, whatever helps you grow.",
      "**Best tools:** Camera gear, editing software, subscriptions — whatever you need.",
      "**Flexible time off:** Take what you need. We trust you.",
      "**High impact:** Small team = your work actually matters. No corporate bullshit."
    ]
  },
  'full-stack-developer': {
    slug: 'full-stack-developer',
    title: 'Full Stack Developer',
    location: 'Remote',
    employmentType: 'Full time',
    locationType: 'Remote',
    department: 'Engineering',
    compensation: 'Private',
    formType: 'engineering',
    intro: [
      "We're building AI Employees that handle every call, text, and message — so businesses never miss another customer.",
      "Voice AI that answers, books, and follows up. 24/7. No hold times. No missed opportunities.",
      "We're building AI that gives people their time back."
    ],
    whyHiring: {
      paragraphs: [
        "We're building the platform that powers AI Employees for thousands of businesses.",
        "Real-time voice AI, intelligent booking, seamless integrations — and we need builders who ship fast.",
        "We need someone who can own features end-to-end and isn't afraid to move fast."
      ]
    },
    whatYoullDo: {
      day30: "Get up to speed on our stack. Ship your first feature. Break something, fix it, learn fast.",
      day60: "Own major features — integrations, dashboard components, API endpoints. You're in the codebase daily.",
      day90: "You're a core part of the Ayra 2.0 launch. Your code is running in production, powering real businesses."
    },
    whoYouAre: [
      "You've built and shipped real products — not just tutorials.",
      "You're comfortable across the stack (React, Node, databases, APIs).",
      "You move fast and fix things. Perfection comes through iteration, not planning.",
      "You can take a vague problem and turn it into working software.",
      "**Bonus:** You've worked with multi-tenant SaaS, voice APIs, or AI products."
    ],
    whyAyra: [
      { label: "Real impact:", text: "Small team = your code actually matters. No bureaucracy." },
      { label: "Equity available:", text: "Opportunity to earn ownership in a company that's actually growing." },
      { label: "Ship daily:", text: "We deploy constantly. Your work goes live fast." },
      { label: "Remote freedom:", text: "Work from anywhere. We care about output, not hours." },
      { label: "Founder access:", text: "Work directly with the CTO and CEO. Your ideas get heard." }
    ],
    whatWeOffer: [
      "**Learning budget:** $10,000/year for courses, tools, conferences — whatever helps you grow.",
      "**Best tools:** MacBook, monitors, software — whatever you need to do your best work.",
      "**Flexible time off:** Take what you need. We trust you.",
      "**High impact:** Ship things that real businesses use every day."
    ]
  },
  'voice-ai-engineer': {
    slug: 'voice-ai-engineer',
    title: 'Voice AI Engineer',
    location: 'Remote',
    employmentType: 'Full time',
    locationType: 'Remote',
    department: 'Engineering',
    compensation: 'Private',
    formType: 'engineering',
    intro: [
      "We're building AI Employees that sound human — not robotic, not awkward, actually human.",
      "Our AI handles real calls for real businesses. Plumbers, dentists, contractors — people who can't afford to miss a call.",
      "The bar is high. Every conversation matters."
    ],
    whyHiring: {
      paragraphs: [
        "Voice AI is only as good as the conversations it can handle.",
        "We need someone who obsesses over prompts, edge cases, and making every call feel natural.",
        "If you've ever yelled at a bad IVR system, this job is your revenge."
      ]
    },
    whatYoullDo: {
      day30: "Dive deep into our voice AI stack (Vapi, Retell, LLMs). Understand how our agents handle calls today.",
      day60: "Own prompt engineering and conversation design. Improve call quality, reduce edge case failures, make Ayra smarter.",
      day90: "You've measurably improved our AI's performance. Calls feel smoother, bookings go up, clients are happier."
    },
    whoYouAre: [
      "You understand LLMs, prompt engineering, and conversation design deeply.",
      "You're obsessive about details — a weird pause or awkward phrase bothers you.",
      "You can debug a bad AI response and figure out why it happened.",
      "You think in edge cases — \"what if the caller says THIS?\"",
      "**Bonus:** Experience with voice APIs (Vapi, Retell, Twilio) or building conversational AI."
    ],
    whyAyra: [
      { label: "Cutting edge:", text: "Work on voice AI that's actually deployed, not a research project." },
      { label: "Equity available:", text: "Opportunity to earn ownership in a company that's actually growing." },
      { label: "Real feedback:", text: "Your improvements get tested on real calls immediately." },
      { label: "Remote freedom:", text: "Work from anywhere. We care about output, not hours." },
      { label: "Founder access:", text: "Work directly with a founder who's spent 2 years obsessing over this." }
    ],
    whatWeOffer: [
      "**Learning budget:** $10,000/year for courses, tools, conferences — whatever helps you grow.",
      "**Best tools:** Whatever you need to do your best work.",
      "**Flexible time off:** Take what you need. We trust you.",
      "**High impact:** Every improvement you make touches real conversations, every day."
    ]
  },
  'customer-success-lead': {
    slug: 'customer-success-lead',
    title: 'Customer Success Lead',
    location: 'Remote',
    employmentType: 'Full time',
    locationType: 'Remote',
    department: 'Customer Success',
    compensation: 'Private',
    formType: 'customer-facing',
    intro: [
      "We're building AI Employees that handle every call, text, and message — so businesses never miss another customer.",
      "We serve home services businesses, healthcare practices, and local businesses who need to answer every call.",
      "Our product is powerful — but clients need help getting the most out of it."
    ],
    whyHiring: {
      paragraphs: [
        "Our LTV depends on clients succeeding, not just signing up.",
        "Voice AI isn't plug-and-play. Clients need onboarding, prompt tuning, and ongoing support.",
        "We need someone who owns the client experience end-to-end and makes sure nobody churns."
      ]
    },
    whatYoullDo: {
      day30: "Learn the product inside out. Shadow onboarding calls. Understand what makes clients succeed (and fail).",
      day60: "Own client onboarding. Set up new accounts, tune prompts, train clients on the platform. Be the reason they love us.",
      day90: "You've built the playbook. Onboarding is smooth, clients are happy, churn is down. You're identifying upsell opportunities."
    },
    whoYouAre: [
      "You genuinely like helping people succeed.",
      "You're organized, proactive, and don't let things slip through the cracks.",
      "You can explain technical concepts to non-technical people without being condescending.",
      "You stay calm when clients are frustrated and turn problems into wins.",
      "**Bonus:** Experience with SaaS onboarding, voice AI, or home services businesses."
    ],
    whyAyra: [
      { label: "Own the experience:", text: "You're not a ticket-taker. You own the entire client journey." },
      { label: "Equity available:", text: "Opportunity to earn ownership in a company that's actually growing." },
      { label: "Real impact:", text: "Every client you save directly protects revenue." },
      { label: "Remote freedom:", text: "Work from anywhere. We care about output, not hours." },
      { label: "Founder access:", text: "Work directly with the CEO on client strategy." }
    ],
    whatWeOffer: [
      "**Learning budget:** $10,000/year for courses, tools, conferences — whatever helps you grow.",
      "**Best tools:** Whatever you need to do your best work.",
      "**Flexible time off:** Take what you need. We trust you.",
      "**High impact:** You're the reason clients stay and grow with us."
    ]
  },
  'sales-development-rep': {
    slug: 'sales-development-rep',
    title: 'Sales Development Rep',
    location: 'Remote',
    employmentType: 'Full time',
    locationType: 'Remote',
    department: 'Sales',
    compensation: 'Private',
    formType: 'customer-facing',
    intro: [
      "We're building AI Employees that handle every call, text, and message — so businesses never miss another customer.",
      "We sell to businesses who are tired of missing calls and losing customers to competitors.",
      "The product sells itself — we just need to get in front of the right people."
    ],
    whyHiring: {
      paragraphs: [
        "We've grown to six figures with the founder doing all the sales.",
        "Now we need someone to fill the pipeline so we can scale.",
        "If you can book meetings, we can close them."
      ]
    },
    whatYoullDo: {
      day30: "Learn the product, the pitch, and the market. Shadow sales calls. Start outreach.",
      day60: "Own outbound prospecting. Cold calls, cold emails, LinkedIn — whatever works. Book demos on the calendar.",
      day90: "You've built a repeatable pipeline. Meetings are flowing, deals are closing, and you're a core part of the growth engine."
    },
    whoYouAre: [
      "You're not afraid to pick up the phone or send a cold email.",
      "You handle rejection well — it's just part of the game.",
      "You're organized and follow up relentlessly (without being annoying).",
      "You can communicate value quickly and clearly.",
      "**Bonus:** Experience selling SaaS, AI, or to small businesses / agencies."
    ],
    whyAyra: [
      { label: "Uncapped potential:", text: "We're early. Top performers will grow with the company." },
      { label: "Equity available:", text: "Opportunity to earn ownership in a company that's actually growing." },
      { label: "Product that sells:", text: "When people see Ayra, they get it. Your job is getting them to see it." },
      { label: "Remote freedom:", text: "Work from anywhere. We care about output, not hours." },
      { label: "Founder access:", text: "Work directly with the CEO on sales strategy." }
    ],
    whatWeOffer: [
      "**Learning budget:** $10,000/year for courses, tools, conferences — whatever helps you grow.",
      "**Best tools:** CRM, dialers, whatever you need to crush it.",
      "**Flexible time off:** Take what you need. We trust you.",
      "**High impact:** You're directly responsible for revenue growth."
    ]
  },
  'growth-marketing': {
    slug: 'growth-marketing',
    title: 'Growth Marketing',
    location: 'Remote',
    employmentType: 'Full time',
    locationType: 'Remote',
    department: 'Marketing',
    compensation: 'Private',
    formType: 'marketing',
    intro: [
      "Ayra AI builds AI Employees that act like real team members — answering every call 24/7, booking appointments, following up on missed calls and quotes, and handling customer inquiries in real time.",
      "We've grown to six-figure revenue with zero marketing spend, serving home services businesses, healthcare practices, and local businesses who can't afford to miss a customer. We're building the AI-native communication layer for the $600B home services industry — and we're just getting started."
    ],
    whyHiring: {
      paragraphs: [
        "**Build from 0→1:** You'll own growth marketing strategy and execution across all channels, starting from first principles.",
        "**Real Customers, Real Revenue:** Everything you do will directly impact bottom-line metrics.",
        "**Work Directly with Founders:** Move fast, iterate quickly, and make real decisions with the people building the product.",
        "**Create a Category:** Help define how AI Employees are bought, understood, and adopted by businesses who've never used AI before."
      ]
    },
    whatYoullDo: {
      sections: [
        {
          title: "Growth & Demand Gen",
          items: [
            "Own and optimize the full funnel — from first impression to qualified lead to closed customer.",
            "Launch and manage paid campaigns (Meta, Google, LinkedIn, YouTube, etc.), including creative development and audience testing.",
            "Build, test, and optimize landing pages, email flows, and CTAs for conversion.",
            "Set up tracking infrastructure (GA4, Segment, HubSpot, etc.) and own performance reporting and attribution models."
          ]
        },
        {
          title: "Product & Content Marketing",
          items: [
            "Own positioning, messaging, and competitive insights for key features and use cases.",
            "Partner with Product and Sales to plan feature launches, write enablement materials, and train GTM teams.",
            "Craft high-converting product marketing collateral — one-pagers, case studies, and demos."
          ]
        },
        {
          title: "Social, Community & Brand",
          items: [
            "Run our social channels (LinkedIn, Instagram, YouTube) to grow audience and drive engagement.",
            "Work with contractors or agencies (if needed) to produce UGC-style and brand content.",
            "Experiment with organic channels (SEO, community, influencer, referral loops) to find scalable acquisition."
          ]
        },
        {
          title: "Data, Tooling & Automation",
          items: [
            "Set up lead scoring, email automation, and CRM integrations (e.g., HubSpot, GoHighLevel).",
            "Define KPIs and report on funnel metrics weekly and monthly.",
            "Use tools like Apollo, Zapier, and Webflow to automate outreach, enrichment, and onboarding."
          ]
        }
      ]
    },
    whoYouAre: [
      "3+ years in product marketing, growth marketing, or full-stack marketing at a high-growth B2B or SaaS startup.",
      "Deep knowledge of performance marketing, attribution, and funnel optimization.",
      "Ability to write clearly and persuasively for multiple audiences (business owners, operators, decision-makers).",
      "Comfortable working with low design support — you can mock things up in Figma, Canva, or Webflow when needed.",
      "Strong understanding of marketing analytics tools (GA4, Looker Studio, HubSpot, etc.).",
      "**Bonus:** Experience marketing AI, SaaS, or vertical software products.",
      "**Bonus:** Familiarity with sales-led and product-led growth motions."
    ],
    whyAyra: [
      { label: "Ground floor:", text: "You're not joining a marketing team. You ARE the marketing team. Build it from scratch." },
      { label: "Equity available:", text: "Opportunity to earn ownership in a company that's actually growing." },
      { label: "Ship daily:", text: "No approval chains. No committees. Launch, learn, iterate." },
      { label: "Remote freedom:", text: "Work from anywhere. We care about output, not hours." },
      { label: "Founder access:", text: "Work directly with the CEO. Your ideas get heard immediately." }
    ],
    whatWeOffer: [
      "**Learning budget:** $10,000/year for courses, tools, conferences — whatever helps you grow.",
      "**Best tools:** Software, subscriptions, whatever you need to execute.",
      "**Flexible time off:** Take what you need. We trust you.",
      "**High impact:** Small team = your work actually matters. No corporate bullshit."
    ],
    customFormQuestion: {
      label: "What's the best campaign you've ever run? (brief description)",
      placeholder: "Describe your most successful marketing campaign..."
    }
  },
  'account-executive': {
    slug: 'account-executive',
    title: 'Account Executive',
    location: 'Remote (US/Canada)',
    employmentType: 'Full time',
    locationType: 'Remote',
    department: 'Sales',
    compensation: 'Private',
    formType: 'customer-facing',
    intro: [
      "Ayra AI is building AI Employees for businesses that handle every call, text, and message — so they never miss another customer. Running a home services business, dental practice, or local company is chaotic — calls, scheduling, follow-ups, missed opportunities. We automate all of it so business owners can focus on what they do best: serving customers.",
      "Our AI Employees answer phones 24/7, book appointments in real time, follow up on leads, and handle customer inquiries — sounding human, not robotic.",
      "We've grown to six-figure revenue with zero marketing spend, and we're ready to scale aggressively."
    ],
    whyHiring: {
      paragraphs: [
        "We move fast. We talk to customers every day.",
        "At Ayra, we value:",
        "**Customer Empathy** — We feel our customers' pain and build with their problems in mind.",
        "**Speed** — We move fast because speed forces simplicity and sharp focus on what truly matters.",
        "**Ownership** — We take responsibility, ship things that matter, and never wait for permission."
      ]
    },
    whatYoullDo: {
      sections: [
        {
          title: "About the Role",
          items: [
            "As an Account Executive at Ayra AI, you'll help define how AI transforms small and medium businesses. You'll close deals with home services companies, healthcare practices, and local business owners — bringing our AI Employees into their day-to-day operations. Your work directly drives revenue, shapes our GTM motion, and helps us scale rapidly."
          ]
        },
        {
          title: "Your Core Responsibilities",
          items: [
            "Manage & Close Inbound Leads: Convert a steady stream of high-intent demo requests into paying customers.",
            "Outbound Prospecting: Source new leads via cold calling, cold email, LinkedIn, and industry events.",
            "Solution Selling: Understand customer pain points and recommend the right Ayra AI solution.",
            "Pipeline & Metrics Ownership: Track conversion rates, maintain clean pipeline hygiene, and consistently hit revenue goals.",
            "Cross-Team Collaboration: Provide feedback to Product and Customer Success to improve messaging and feature alignment.",
            "Objection Handling & Closing: Control the deal, close with confidence, and know when to push or walk away.",
            "Run Demos: Show business owners exactly how Ayra will change their operations — and get them excited."
          ]
        }
      ]
    },
    whoYouAre: [
      "Have 3+ years of closing experience in SMB or vertical SaaS sales (selling to business owners is a big plus)",
      "Are a full-stack sales athlete — you can sell, prospect, and refine your talk track on the fly",
      "Know how to sell to non-technical buyers with empathy and simplicity",
      "Thrive in fast-paced environments and get excited by urgency and ownership",
      "Are data-driven and track your own metrics and performance like a pro",
      "Willing to travel to industry events and own deals end-to-end",
      "**Bonus:** Experience selling AI, voice, or automation products",
      "**Bonus:** Background in home services, healthcare, or SMB sales"
    ],
    whyAyra: [
      { label: "Massive Market:", text: "AI for small businesses is exploding, and we're building the category leader." },
      { label: "Product That Sells:", text: "When business owners hear Ayra handle a call, they get it immediately." },
      { label: "Ground Floor Opportunity:", text: "Join early, grow with the company, and shape how we go to market." },
      { label: "Equity Available:", text: "Opportunity to earn real ownership in a company that's actually growing." },
      { label: "Uncapped Earnings:", text: "Competitive base + uncapped commission." },
      { label: "Remote Freedom:", text: "Work from anywhere. We care about results, not hours." },
      { label: "Founder Access:", text: "Work directly with the CEO on sales strategy and GTM." }
    ],
    whatWeOffer: [
      "**Learning budget:** $10,000/year for courses, tools, conferences — whatever helps you grow.",
      "**Best tools:** CRM, dialers, prospecting tools — whatever you need to crush it.",
      "**Flexible time off:** Take what you need. We trust you.",
      "**High impact:** You're directly responsible for revenue growth. Your work matters."
    ],
    customFormQuestion: {
      label: "Tell us about your best sales win",
      placeholder: "Describe your most memorable sales win..."
    }
  },
  'integrations-engineer': {
    slug: 'integrations-engineer',
    title: 'Integrations Engineer',
    location: 'Remote',
    employmentType: 'Full time',
    locationType: 'Remote',
    department: 'Engineering',
    compensation: 'Private',
    formType: 'engineering',
    intro: [
      "Ayra AI builds AI Employees that handle every call, text, and message — so businesses never miss another customer. Our AI answers phones 24/7, books jobs, follows up on leads, and syncs everything directly to the field service tools home services businesses already use.",
      "The magic happens when Ayra connects seamlessly to the systems our customers rely on — their field service platforms, CRMs, calendars, and workflows. That's where you come in."
    ],
    whyHiring: {
      paragraphs: [
        "Our customers use field service management tools like Jobber, Housecall Pro, and ServiceTitan — plus CRMs, calendars, and phone systems. For Ayra to feel like a real employee, it needs to plug into their existing stack perfectly.",
        "We need someone who lives and breathes APIs, webhooks, and third-party integrations. Someone who can build rock-solid connections that just work — and debug the ones that don't."
      ]
    },
    whatYoullDo: {
      day30: "Get deep into our integration architecture. Understand how we connect to voice platforms (Vapi, Retell), field service tools, and CRMs. Ship your first integration improvement.",
      day60: "Own the integration roadmap. Build new connections to platforms like Jobber, Housecall Pro, and ServiceTitan. Improve reliability and reduce onboarding time.",
      day90: "You've become the integrations expert. New platform connections ship fast, existing ones are bulletproof, and customers are amazed at how seamlessly Ayra fits into their workflow."
    },
    coreResponsibilities: [
      "**Build & Maintain Integrations:** Connect Ayra to field service platforms (Jobber, Housecall Pro, ServiceTitan), CRMs (GoHighLevel, HubSpot), calendars, and phone systems.",
      "**API Development:** Design and build robust APIs that partners and customers can use to extend Ayra's capabilities.",
      "**Webhooks & Event Handling:** Build reliable event-driven systems that keep data in sync across platforms in real-time.",
      "**Debug & Troubleshoot:** When an integration breaks at 2am, figure out why and make sure it never happens again.",
      "**Documentation:** Write clear integration docs that customers and internal teams actually want to read.",
      "**Work with Customers:** Jump on calls with customers to understand their tech stack and build solutions that fit.",
      "**Automation & Workflows:** Build Zapier/Make integrations and internal automation that eliminates manual work."
    ],
    whoYouAre: [
      "Have 3+ years of experience building integrations, working with third-party APIs, or backend development focused on connectivity.",
      "Can read API docs and have a working integration running in hours, not weeks.",
      "Have experience with REST APIs, webhooks, OAuth, and authentication flows.",
      "Are comfortable with Node.js, Python, or similar backend languages.",
      "Know how to handle rate limits, retries, error handling, and all the messy realities of third-party APIs.",
      "Can debug a broken integration with limited information and figure out if it's our code, their API, or something in between.",
      "**Bonus:** Experience with home services platforms — Jobber, Housecall Pro, ServiceTitan, or similar field service management APIs.",
      "**Bonus:** Experience with voice/telephony APIs (Twilio, Vapi, Retell).",
      "**Bonus:** You've built Zapier/Make integrations or worked on an integration marketplace."
    ],
    whyAyra: [
      { label: "Own the Integration Layer:", text: "You're not maintaining someone else's spaghetti code. You're building our integration architecture from the ground up." },
      { label: "Equity Available:", text: "Opportunity to earn real ownership in a company that's actually growing." },
      { label: "Real Impact:", text: "Every integration you build directly unblocks sales and makes customers successful." },
      { label: "Variety:", text: "You'll work with dozens of different platforms and APIs — never boring." },
      { label: "Remote Freedom:", text: "Work from anywhere. We care about output, not hours." },
      { label: "Founder Access:", text: "Work directly with the CTO and CEO. Your ideas get heard immediately." }
    ],
    whatWeOffer: [
      "**Learning budget:** $10,000/year for courses, tools, API subscriptions — whatever helps you grow.",
      "**Best tools:** MacBook, monitors, software, API access — whatever you need to build.",
      "**Flexible time off:** Take what you need. We trust you.",
      "**High impact:** Small team = your integrations power real businesses every single day."
    ],
    customFormQuestion: {
      label: "What's the most complex integration you've built?",
      placeholder: "Describe the most complex integration project you've worked on..."
    }
  }
};

export const getJobBySlug = (slug: string): JobData | undefined => {
  return jobListings[slug];
};

export const getAllJobSlugs = (): string[] => {
  return Object.keys(jobListings);
};
